// Clima de la obra (Open-Meteo, el mismo servicio que usa la pestaña Clima de LisualPro).
// Lluvia (mm), temperatura y tipo de tiempo (código WMO) por hora y por día, para cruzarlo con la actividad en el gráfico.
import { getObraByFeedID, saveObraUbicacion } from '../../../obras/api/obras.api';
import { addDays, todayKey } from './iaData';
import { historico, pronostico } from '../weather/clima/meteo';

const cache = new Map();

// Ubicación de la obra:
//  1) la que está guardada en la obra (Firebase, ubicacionMaps): es la que el cliente puede cambiar desde LisualPro
//  2) la de la cámara (coordinates)
//  3) caché local: lo último que se leyó/guardó para esta cámara (solo si no se pudo leer Firebase: sin red, sin permiso)
// Devuelve { lat, lng, label, direccion, source } o null si no hay forma de saberla (se le pide al cliente).
const cacheKey = (feedId, cam) => 'obraLoc:' + (feedId || cam);
function readCache(feedId, cam) {
  try {
    const c = JSON.parse(localStorage.getItem(cacheKey(feedId, cam)) || 'null');
    if (c && c.lat != null) return { ...c, source: 'cache' };
    const legacy = cam && JSON.parse(localStorage.getItem('iaDash:loc:' + cam) || 'null'); // lo que antes se elegía solo en este navegador
    if (legacy && legacy.lat != null) return { ...legacy, source: 'cache' };
  } catch (e) { /* sin storage */ }
  return null;
}
export function saveLocation(cam, loc, feedId) {
  try { localStorage.setItem(cacheKey(feedId, cam), JSON.stringify({ lat: loc.lat, lng: loc.lng, label: loc.label, direccion: loc.direccion })); } catch (e) { /* sin storage */ }
}

export async function resolveLocation({ coords, feedId, cam }) {
  let firebaseRespondio = false; // si Firebase contestó (aunque sea "no hay"), la caché no manda
  if (feedId) {
    try {
      const o = await getObraByFeedID(feedId);
      const u = o?.ubicacionMaps;
      if (u && u.lat != null && u.lng != null) {
        const loc = { lat: +u.lat, lng: +u.lng, label: o.direccion || o.nombre || 'Ubicación de la obra', direccion: o.direccion || '', source: 'obra' };
        saveLocation(cam, loc, feedId);
        return loc;
      }
      firebaseRespondio = true;
    } catch (e) {
      if (e?.response?.status === 404) firebaseRespondio = true; // la cámara no tiene obra todavía
    }
  }
  if (coords && coords.lat != null && coords.lng != null && coords.lat !== '' && coords.lng !== '') {
    return { lat: +coords.lat, lng: +coords.lng, label: 'Ubicación de la cámara', direccion: '', source: 'camara' };
  }
  return firebaseRespondio ? null : readCache(feedId, cam);
}

// El cliente ubicó la obra en el mapa: se guarda en Firebase (obra de la cámara) y queda en caché.
export async function saveObraLocation({ feedId, cam, lat, lng, direccion }) {
  if (!feedId) throw new Error('Falta la cámara');
  const o = await saveObraUbicacion(feedId, { lat, lng, direccion });
  const u = o?.ubicacionMaps || { lat, lng };
  const dir = o?.direccion || direccion || '';
  const loc = { lat: +u.lat, lng: +u.lng, label: dir || o?.nombre || 'Ubicación de la obra', direccion: dir, source: 'obra' };
  saveLocation(cam, loc, feedId);
  try { if (cam) localStorage.removeItem('iaDash:loc:' + cam); } catch (e) { /* */ }
  window.dispatchEvent(new CustomEvent('obra:ubicacion', { detail: { feedId, loc } }));
  return loc;
}

export async function searchPlace(q) {
  const r = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(q)}&count=6&language=es&format=json`);
  const j = await r.json();
  return (j.results || []).map((x) => ({ lat: x.latitude, lng: x.longitude, label: [x.name, x.admin2 !== x.name && x.admin2, x.admin1, x.country].filter(Boolean).join(', ') }));
}

// Dirección completa (calle y número) con Nominatim de OpenStreetMap. Su política no permite autocompletar:
// se llama solo cuando el usuario aprieta Buscar / Enter.
export async function searchAddress(q) {
  const r = await fetch(`https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1&limit=6&accept-language=es&q=${encodeURIComponent(q)}`);
  if (!r.ok) return [];
  const j = await r.json();
  const seen = new Set();
  return (j || []).map((x) => {
    const a = x.address || {};
    const calle = [a.road || a.pedestrian || a.name, a.house_number].filter(Boolean).join(' ');
    const zona = a.suburb || a.neighbourhood || a.city_district || a.village || a.town;
    const ciudad = a.city || a.town || a.municipality || a.state_district;
    const label = [calle, zona, ciudad !== zona && ciudad, a.state !== ciudad && a.state].filter(Boolean).join(', ')
      || String(x.display_name || '').split(', ').slice(0, 4).join(', ');
    return { lat: +x.lat, lng: +x.lon, label, street: !!(a.road && a.house_number) || x.category === 'building' };
  }).filter((x) => (seen.has(x.label) ? false : seen.add(x.label)));
}

// Horas del período: Map { 'YYYY-MM-DDTHH:00': { t, p, c } } en hora argentina (c = código WMO).
// Además trae los días: hours.days = Map { 'YYYY-MM-DD': { c, p, max, min } }
// Usa el mismo cliente que la pestaña Clima (meteo.js): días pasados por mes (forecast con start_date para los
// últimos ~3 meses, archivo histórico para lo anterior), 3 intentos ante 429/5xx/respuestas rotas y caché en
// localStorage de los meses cerrados. Hoy (y lo que venga) sale del pronóstico.
// Antes cada período pedía un rango suelto con un solo reintento y sin caché: al elegir una fecha pasada
// alcanzaba con un 429 o un "timeoutReached" de Open-Meteo para que el gráfico quedara sin clima.
const AR_OFF_S = -3 * 3600;
// Las horas de Open-Meteo vienen en la hora local de la obra (timezone=auto). El gráfico trabaja en hora argentina.
const aHoraAR = (t, offset) => {
  if (!offset || offset === AR_OFF_S) return t;
  const ms = Date.parse(t + ':00Z') - offset * 1000 + AR_OFF_S * 1000;
  return new Date(ms).toISOString().slice(0, 13) + ':00';
};
export async function loadWeather(loc, fromKey, toKey) {
  const ck = `${loc.lat.toFixed(3)},${loc.lng.toFixed(3)}|${fromKey}|${toKey}`;
  const hit = cache.get(ck);
  if (hit && Date.now() - hit.at < 15 * 60e3) return hit.p;
  const p = (async () => {
    const today = todayKey();
    const ayer = addDays(today, -1);
    const [pas, fc] = await Promise.all([
      fromKey <= ayer ? historico(loc, fromKey, toKey < ayer ? toKey : ayer, today) : null,
      toKey >= ayer ? pronostico(loc) : null, // el pronóstico trae ayer y hoy: tapa los huecos del día anterior
    ]);
    const out = new Map();
    out.days = new Map();
    [pas, fc].filter(Boolean).forEach((src) => {
      (src.horas || []).forEach((x) => {
        if (x.temp == null || x.fecha < fromKey || x.fecha > toKey) return;
        const k = aHoraAR(x.t, src.offset);
        if (src === fc && out.has(k)) return; // lo medido (pasado) manda sobre el pronóstico
        out.set(k, { t: x.temp, p: x.mm || 0, c: x.code ?? null });
      });
      (src.dias || []).forEach((d) => {
        if (d.code == null || d.fecha < fromKey || d.fecha > toKey) return;
        if (src === fc && out.days.has(d.fecha)) return;
        out.days.set(d.fecha, { c: d.code, p: d.mm || 0, max: d.tmax, min: d.tmin });
      });
    });
    return out;
  })();
  cache.set(ck, { at: Date.now(), p });
  p.catch(() => cache.delete(ck)); // un error no queda guardado: el próximo intento vuelve a pedir
  return p;
}

// Tipo de tiempo a partir del código WMO de Open-Meteo (y los mm, para separar chaparrón de lluvia fuerte).
// El ícono 3D y el "cielo" de fondo de cada tipo están en ./clima. rank = qué tan notable es (lo notable siempre se muestra en el celular).
export const WX_KINDS = {
  sol: { label: 'Soleado', rank: 0 },
  noche: { label: 'Noche despejada', rank: 0 },
  parcial: { label: 'Parcialmente nublado', rank: 1 },
  nublado: { label: 'Nublado', rank: 1 },
  niebla: { label: 'Niebla', rank: 2 },
  chaparron: { label: 'Lloviznas o chaparrones', rank: 3 },
  lluvia: { label: 'Lluvia', rank: 4 },
  nieve: { label: 'Nieve', rank: 4 },
  tormenta: { label: 'Tormenta', rank: 5 },
};
export const WX_ORDER = ['sol', 'parcial', 'nublado', 'niebla', 'chaparron', 'lluvia', 'tormenta', 'nieve', 'noche'];

// Intensidad de la lluvia (1 = pocas gotas, 3 = lluvia fuerte) según los mm del día o de la hora
const rainLevel = (mm, perHour) => (perHour ? (mm < 0.5 ? 1 : mm < 2.5 ? 2 : 3) : (mm < 5 ? 1 : mm < 15 ? 2 : 3));

// mm = lluvia del intervalo; heavy = mm a partir de los cuales una lluvia "de verdad" se pinta como lluvia y no chaparrón
// minMm = por debajo de esto una llovizna no cuenta como lluvia (el código del día marca "lo peor", aunque sean 0,2 mm)
export function wxKind(code, mm = 0, { night = false, heavy = 5, minMm = 0 } = {}) {
  if (code == null) return null;
  if (code >= 95) return 'tormenta';
  if (((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) && mm < minMm) return 'nublado';
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return 'nieve';
  if (code >= 51 && code <= 67) return code >= 61 && mm >= heavy ? 'lluvia' : (code >= 63 ? 'lluvia' : 'chaparron');
  if (code >= 80 && code <= 82) return code === 82 || mm >= heavy ? 'lluvia' : 'chaparron';
  if (code === 45 || code === 48) return 'niebla';
  if (code === 3) return 'nublado';
  if (code === 2) return 'parcial';
  return night ? 'noche' : 'sol';
}

const deg = (v) => (v == null ? null : Math.round(v));

// Suma de lluvia y temperatura promedio (de 7 a 19 h) para cada intervalo del gráfico.
// En días y horas además agrega `wx` = { kind, label, rank, level, night, detail } para el ícono y el cielo de la columna.
export function weatherForRows(rows, gran, hours) {
  if (!hours) return rows;
  const AR = 3 * 3600e3;
  return rows.map((r) => {
    const from = gran === 'foto' ? r.start - 30 * 60e3 : r.start;
    const to = gran === 'foto' ? r.start + 30 * 60e3 : r.end;
    let p = 0, ts = 0, n = 0;
    for (let ms = Math.floor(from / 3600e3) * 3600e3; ms < to; ms += 3600e3) {
      const k = new Date(ms - AR).toISOString().slice(0, 13) + ':00';
      const w = hours.get(k);
      if (!w) continue;
      p += w.p;
      const h = new Date(ms - AR).getUTCHours();
      if (gran === 'hora' || gran === 'foto' || (h >= 7 && h < 19)) { ts += w.t; n += 1; }
    }
    const lluvia = +p.toFixed(1);
    const temp = n ? +(ts / n).toFixed(1) : null;
    let wx = null;
    if (gran === 'dia') {
      const k = new Date(r.start - AR).toISOString().slice(0, 10);
      const d = hours.days && hours.days.get(k);
      if (d) {
        const kind = wxKind(d.c, d.p, { heavy: 10, minMm: 1 });
        const bits = [d.p >= 0.1 ? `Lluvia ${+d.p.toFixed(1)} mm` : 'Sin lluvia', d.max != null && `máx ${deg(d.max)}°`, d.min != null && `mín ${deg(d.min)}°`];
        wx = { kind, ...WX_KINDS[kind], level: rainLevel(d.p, false), night: false, detail: bits.filter(Boolean).join(' · ') };
      }
    } else if (gran === 'hora' || gran === 'foto') {
      const ms = Math.floor(r.start / 3600e3) * 3600e3;
      const w = hours.get(new Date(ms - AR).toISOString().slice(0, 13) + ':00');
      if (w) {
        const h = new Date(ms - AR).getUTCHours();
        const night = h < 7 || h >= 20;
        const kind = wxKind(w.c, w.p, { night, heavy: 2, minMm: 0.1 });
        const bits = [w.p >= 0.1 ? `Lluvia ${+w.p.toFixed(1)} mm` : 'Sin lluvia', `${deg(w.t)}°`];
        wx = { kind, ...WX_KINDS[kind], level: rainLevel(w.p, true), night, hour: ms, detail: bits.join(' · ') };
      }
    }
    return { ...r, lluvia, temp, wx };
  });
}
